import React, { useCallback } from 'react';
import clsx from 'clsx';

import { KEYBOARD_EVENTS } from '../../../../../../../../utils/constants/commonConstants';
import { CitationMarkerProps } from './MessagesContainer.types';
import { CITATION_SOURCE_TYPE, CITATION_SURFACE } from './MessagesContainer.constants';
import { citationActionTitle } from './citationUtils';

import styles from './MessagesContainer.module.scss';

/**
 * Renders a clickable superscript citation marker.
 * - Resolves the emitted id to its CANONICAL id so duplicate citations of the same
 *   source share one number + anchor (Perplexity-style), and displays the
 *   reading-order number (first reference in the answer = 1).
 * - document / tool sources: colored, and clickable WHEN a handler is passed →
 *   delegates to onCitationActivate, which opens the Dataroom doc viewer, navigates
 *   to the AICA-report section, or falls back to scroll-to-source — CitationMarker
 *   itself makes no routing decision, so it and SourcesPanel can never diverge.
 *   Without a handler (e.g. an isolated-render test) the marker renders the plain
 *   number only — no role/tabIndex/onClick/title — matching SourcesPanel's own
 *   no-handler degrade, so a caller that forgot to wire citations never ships a
 *   dead-looking button.
 * - model_knowledge: renders NOTHING — model reasoning is never surfaced as a
 *   reference (product decision, 2026-07-13).
 * - No matching source (guardrail-dropped id, stray sentinel, or a marker rendered
 *   mid-stream before `sources` arrives on the final chunk): renders NOTHING, same
 *   as model_knowledge. It previously rendered an inert muted superscript numbered
 *   with the RAW emitted id, which collides with the reading-order numbering of real
 *   sources — a dropped ‹cite:1› and the live source displayed as [1] both rendered
 *   "1", one dead and one clickable. An id that resolves to nothing is not a
 *   reference, so it gets no marker.
 */
const CitationMarker: React.FC<CitationMarkerProps> = ({
    id,
    msgId,
    sources,
    idToCanonical,
    displayNumbers,
    canonicalSourcesCount,
    onCitationActivate,
    getCitationTitle,
}) => {
    const canonicalId = idToCanonical?.[id] ?? id;
    const source = sources?.find((s) => s.id === canonicalId);
    const shownNumber = displayNumbers?.[canonicalId] ?? canonicalId;

    const handleClick = useCallback(() => {
        if (!source || source.type === CITATION_SOURCE_TYPE.MODEL_KNOWLEDGE) return;

        onCitationActivate?.(source, CITATION_SURFACE.MARKER, {
            msgId,
            canonicalId,
            sourcesCount: canonicalSourcesCount ?? sources?.length,
            shownNumber,
        });
    }, [source, msgId, canonicalId, sources, canonicalSourcesCount, shownNumber, onCitationActivate]);

    // Unresolvable id → no marker (see the doc comment): never a dead superscript
    // wearing a number that belongs to a real source.
    if (!source) return null;

    if (source.type === CITATION_SOURCE_TYPE.MODEL_KNOWLEDGE) {
        // Product decision (2026-07-13): model reasoning is never surfaced as a
        // reference — no marker in the answer, no entry in the Sources panel.
        return null;
    }

    const interactiveProps = onCitationActivate
        ? {
              onClick: handleClick,
              role: 'button' as const,
              tabIndex: 0,
              onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) {
                      // <sup> isn't natively interactive — stop Space from scrolling the page
                      e.preventDefault();
                      handleClick();
                  }
              },
              title: getCitationTitle ? getCitationTitle(source) : citationActionTitle(source),
          }
        : {};

    return (
        <sup
            className={clsx(
                styles.CitationMarker,
                source.type === CITATION_SOURCE_TYPE.DOCUMENT ? styles.CitationMarkerDoc : styles.CitationMarkerTool,
            )}
            data-testid={`citation-${canonicalId}`}
            {...interactiveProps}
        >
            {shownNumber}
        </sup>
    );
};

export default CitationMarker;
